import {asn1, pkcs12, pki, util} from 'node-forge'
import {
  PlatformAndroidApiKey,
  PlatformAndroidToken,
  SnsPlatform
} from '../../../common/types'

export const isIOS = (platformCode: SnsPlatform) => {
  return !!platformCode.match(/^APNS/)
}

export const isAndroidFcmHttpV1 = (
  attributes: PlatformAndroidApiKey | PlatformAndroidToken
): attributes is PlatformAndroidToken => 'token' in attributes

export const isAndroidLegacyApiKey = (
  attributes: PlatformAndroidApiKey | PlatformAndroidToken
): attributes is PlatformAndroidApiKey => 'apiKey' in attributes

export const isAndroid = (platformCode: SnsPlatform) => platformCode === 'GCM'

export const decodePKCS12 = (
  file: File,
  password: string | undefined
): Promise<{certificate: string; privateKey: string}> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    // eslint-disable-next-line functional/immutable-data
    reader.onload = evt => {
      try {
        const result: unknown = evt && evt.target ? evt.target.result : null
        if (!result) {
          return reject(new Error('No file data'))
        }
        const binary = result as util.ByteStringBuffer
        const p12Asn1 = asn1.fromDer(binary)
        const p12 = pkcs12.pkcs12FromAsn1(p12Asn1, undefined, password)

        const certBags = p12.getBags({bagType: pki.oids.certBag})
        const pkeyBags = p12.getBags({bagType: pki.oids.pkcs8ShroudedKeyBag})

        if (!certBags || !pkeyBags) {
          return reject(
            new Error('Unable to parse key and certificate in file')
          )
        }

        const cert = pki.oids.certBag in certBags && certBags[pki.oids.certBag]
        const certBag = cert && cert.length ? cert[0] : undefined

        const key =
          pki.oids.pkcs8ShroudedKeyBag in pkeyBags &&
          pkeyBags[pki.oids.pkcs8ShroudedKeyBag]
        const keyBag = key && key.length ? key[0] : undefined

        if (certBag && certBag.cert && keyBag && keyBag.key) {
          const certificate = pki.certificateToPem(certBag.cert)
          const privateKey = pki.privateKeyToPem(keyBag.key)
          return resolve({certificate, privateKey})
        }
        return reject(
          new Error('Unable to find key and certificate properties in file ')
        )
      } catch (e) {
        reject(e)
      }
    }
    reader.onerror = reject
    reader.readAsBinaryString(file)
  })
}
